import {
  Box,
  Button,
  Card,
  Inline,
  Link,
  List,
  Modal,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import type { InvoiceReport } from '@omnipresentgroup/types';
import { useMemo } from 'react';
import GoCardlessBrandLogo from './../../../../assets/go-cardless-brand-logo.png';
import { useManagerUser } from '../../../hooks';
import { WrappedMixpanel } from '../../../mixpanelWrapper';

export const DirectDebitModal = ({
  closeFn,
  open,
  url,
  invoice,
}: {
  closeFn: () => void;
  open?: boolean;
  url: string;
  invoice?: InvoiceReport;
}) => {
  const { userId, companyId } = useManagerUser();

  const mixPanelProperties = useMemo(
    () => ({
      companyId,
      userId: userId,
      invoiceId: invoice?.id,
      memo: invoice?.reference,
    }),
    [companyId, userId, invoice],
  );

  return (
    <Modal open={open} span={5} onOpenChange={closeFn}>
      <Modal.Header title="Direct Debit invoice payments" />

      <Modal.Body>
        <Stack justify="space-between">
          <Inline justify="center">
            <img src={GoCardlessBrandLogo} alt="GoCardless logo" />
          </Inline>
          <Typography as="span" size="16">
            We&apos;ve partnered with GoCardless to introduce a hassle-free way
            to manage your payments with us via Direct Debit. With Direct Debit,
            you can authorise Omnipresent to automatically deduct each invoice
            amount from your chosen bank account on the due date, saving you
            time, and ensuring timely payments.
          </Typography>
          <Typography as="span" size="16">
            In some cases, Direct Debit payments can also cost less than
            international transfers.
          </Typography>
          <Typography as="span" size="16" weight="bold">
            To enable direct debits
          </Typography>
          <Card p="16">
            <List
              gap="12"
              items={[
                {
                  id: '1',
                  detail:
                    'Complete your billing details in the GoCardless portal below',
                  subDetail: '',
                },
                {
                  id: '2',
                  detail: 'Wait for the bank connection to become active',
                  subDetail:
                    'Depending on the banking institution, this may take more than a week',
                },
                {
                  id: '3',
                  detail:
                    'All future (and open) invoices will be deducted on each due date',
                  subDetail:
                    'We’ll always email you a few days before so it’s never a surprise.',
                },
              ]}
              keyExtractor="id"
              render={(
                { detail, subDetail }: { detail: string; subDetail?: string },
                index: number,
              ) => (
                <Inline gap="16" align={subDetail ? 'baseline' : 'center'}>
                  <Box
                    h={32}
                    w={32}
                    bg="brand"
                    radius="full"
                    css={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography as="span" weight="medium" size="14">
                      {index + 1}
                    </Typography>
                  </Box>

                  <Stack gap="4">
                    <Typography as="span" size="14" weight="medium">
                      {detail}
                    </Typography>
                    {subDetail && (
                      <Typography as="span" size="14">
                        {subDetail}
                      </Typography>
                    )}
                  </Stack>
                </Inline>
              )}
            />
          </Card>

          <Typography as="span" size="16" weight="bold">
            Need to know more?
          </Typography>
          <Typography as="span" size="16">
            We&apos;ve put together some FAQs on our help page below.
          </Typography>
        </Stack>
      </Modal.Body>

      <Modal.Footer>
        <Link
          size="16"
          icon="ExternalLink"
          onClick={() => {
            WrappedMixpanel.track('Direct Debit View FAQs', mixPanelProperties);
            window.open(
              'https://help.omnipresent.com/en/articles/8489160-sign-up-for-direct-debit-payments',
              '_blank',
              'noopener,noreferrer',
            );
          }}
        >
          View FAQs
        </Link>
        <Inline>
          <Button
            variant="secondary"
            onClick={() => {
              WrappedMixpanel.track(
                'Direct Debit Modal Cancelled',
                mixPanelProperties,
              );
              closeFn();
            }}
          >
            Cancel
          </Button>
          <Button
            icon="ExternalLink"
            onClick={() => {
              WrappedMixpanel.track('Direct Debit Clicked', mixPanelProperties);
              window.open(url, '_blank');
            }}
          >
            Enable Direct Debit
          </Button>
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
